// The console's one dropdown shape, shared by the Workspaces toolbar and
// the Lio issue queue. Lives in its own file because the queue is a child
// of SuperadminConsole — importing it back out of that module would be a
// cycle.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

export type FilterOption = { value: string; label: string };

/**
 * One dropdown shape for the whole toolbar.
 *
 * The console used to mix a hand-built panel (Companies) with two native
 * <select>s, and a native select's open list is drawn by the browser in its
 * own blue-on-white chrome — so three controls sitting side by side never
 * matched. Every filter now renders through this: same trigger, same panel,
 * in the console's emerald.
 */
export function FilterDropdown({
  label,
  value,
  options,
  onChange,
  icon,
  placeholder = "All",
  widthClass = "sm:w-44",
  searchable = false,
  searchPlaceholder = "Search…",
  emptyText = "No match.",
  centered = false,
  multiple = false,
  values,
  onChangeMultiple,
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  icon?: ReactNode;
  placeholder?: string;
  widthClass?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Open the list as a centred overlay instead of hanging it off the
   *  trigger. For a control inside a scrolling list: an anchored panel is
   *  clipped by the scroller and by the card it sits in, which is exactly
   *  what happened to the Lio queue's per-row assign dropdown. */
  centered?: boolean;
  /** Pick several. `values`/`onChangeMultiple` replace `value`/`onChange`:
   *  rows become checkboxes, picking one does NOT close the panel, and the
   *  trigger summarises the selection. Used for an issue's assignees, where
   *  a Lio gap is often a backend change AND a prompt change. */
  multiple?: boolean;
  values?: string[];
  onChangeMultiple?: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  // Outside click / Escape closes it. Bound only while open, so a shut
  // dropdown holds no document listeners.
  useEffect(() => {
    if (!open) return;
    // Skipped when centred: that panel is rendered outside boxRef, so this
    // would treat every click inside it as a click away. Its backdrop closes
    // it instead; Escape works either way.
    const onDoc = (e: MouseEvent) => {
      if (centered) return;
      if (boxRef.current && !boxRef.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, centered]);

  // The search box filters in here rather than in the caller, so a caller
  // only ever hands over the full option list.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const selected = new Set(values || []);
  const current = options.find((o) => o.value === value);
  const triggerLabel = multiple
    ? selected.size === 0
      ? placeholder
      : selected.size === 1
        ? options.find((o) => selected.has(o.value))?.label || placeholder
        : `${selected.size} selected`
    : current?.label || placeholder;

  const pick = (v: string) => {
    if (multiple) {
      // Stays open: picking several and closing between each one is the
      // whole reason this mode exists. The query is kept too, so ticking
      // two people out of a filtered list doesn't mean retyping.
      const next = new Set(selected);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChangeMultiple?.([...next]);
      return;
    }
    onChange(v);
    setQuery("");
    setOpen(false);
  };

  const panel = (
    <>
      {searchable && (
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>
        </div>
      )}
      <div className={`${centered ? "max-h-[60vh]" : "max-h-56"} overflow-y-auto py-1`}>
        {shown.map((o) => {
          const active = multiple ? selected.has(o.value) : o.value === value;
          return (
            <button
              key={o.value || "__all__"}
              type="button"
              onClick={() => pick(o.value)}
              className={`w-full flex items-center justify-between gap-2 text-left px-3 ${
                centered ? "py-2 text-sm" : "py-1.5 text-xs"
              } hover:bg-emerald-50 ${
                active ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-gray-600"
              }`}
            >
              {multiple && (
                <span
                  aria-hidden
                  className="shrink-0 w-4 h-4 rounded flex items-center justify-center"
                  style={{
                    border: `1px solid ${active ? "#059669" : "#d1d5db"}`,
                    background: active ? "#059669" : "transparent",
                    color: "#fff",
                  }}
                >
                  {active && <Check className="w-3 h-3" />}
                </span>
              )}
              <span className="truncate flex-1">{o.label}</span>
              {!multiple && active && (
                <Check className="w-3.5 h-3.5 shrink-0 text-emerald-600" />
              )}
            </button>
          );
        })}
        {shown.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-400">{emptyText}</p>
        )}
      </div>
    </>
  );

  return (
    <div ref={boxRef} className="relative">
      <span className="absolute -top-2 left-3 px-1 bg-white text-[10px] font-medium text-gray-400 z-10">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        // The label above is a floating notch, not a <label for>, so name the
        // control here — otherwise a screen reader reads the trigger as just
        // its current value ("All") with nothing saying which filter it is.
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full ${widthClass} inline-flex items-center justify-between gap-2 py-2.5 px-3 text-sm rounded-xl border bg-white text-gray-700 transition-colors hover:bg-emerald-50/60 focus:outline-none focus:ring-2 focus:ring-emerald-200 ${
          open ? "border-emerald-400 ring-2 ring-emerald-100" : "border-gray-200"
        }`}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          {icon}
          <span className="truncate">{triggerLabel}</span>
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-emerald-600 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Anchored: z-50, and the toolbar itself is lifted above the scroller —
          the panel used to share z-20 with the sticky column heads, which come
          later in the DOM and so painted straight over the open list. */}
      {open && !centered && (
        <div className="absolute z-50 mt-1 left-0 w-full min-w-[14rem] bg-white rounded-xl border border-emerald-100 shadow-lg overflow-hidden">
          {panel}
        </div>
      )}

      {/* Centred: a fixed overlay, so nothing the trigger sits inside can clip
          it. The anchored panel is the right default, but inside a scrolling
          list it gets cut off by the scroller and by its own card. */}
      {open && centered && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            role="listbox"
            aria-label={label}
            className="w-full max-w-sm bg-white rounded-2xl border border-emerald-100 shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
              {/* The panel is detached from its trigger here, so it has to say
                  what it is on its own. */}
              <span className="text-xs font-semibold text-gray-700">{label}</span>
              {multiple ? (
                // Nothing closes this panel on its own in multi mode, so the
                // way out has to be obvious.
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Done
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {panel}
          </div>
        </div>
      )}
    </div>
  );
}
